import { ScreenSpinner } from "@stenajs-webui/elements";
import { ModalBody, ModalHeader } from "@stenajs-webui/modal";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";

import { BaseModal } from "../../../../common-ui/modal/BaseModal";
import {
  type UseBookingLoaderResult,
  useDetailedBooking,
} from "../../../../common/bookings/useDetailedBooking";
import { useAppNavigate } from "../../../../common/hooks/useAppNavigate";
import { usePageTitle } from "../../../../common/routes/PageTitle";
import type { DetailedBookingFragment } from "../../../../gql/graphql";
import { useActiveRow } from "../../grid/hooks/useActiveRow";
import {
  BookingErrorModalContents,
  getErrorModalContentFromGQLError,
} from "./BookingErrorModalContents";

export interface BookingDetailsResultProps {
  booking: DetailedBookingFragment;
  onRequestClose: () => void;
}

interface BookingDetailsModalLoaderProps extends Pick<
  ModalContentProps,
  "render"
> {
  className: string;
  getAriaLabel: (bookingNo: number) => string;
}

export function BookingDetailsModalLoader({
  className,
  getAriaLabel,
  ...props
}: BookingDetailsModalLoaderProps) {
  const { bookingNo: bookingNoString } = useParams<"bookingNo">();
  const bookingNo = Number(bookingNoString);
  const bookingLoaderProps = useDetailedBooking(bookingNo);
  const { pop } = useAppNavigate();

  usePageTitle(`Booking ${bookingNo}`);
  useActiveRow({ bookingNo });

  return (
    <BaseModal
      className={className}
      onRequestClose={pop}
      aria-label={getAriaLabel(bookingNo)}
    >
      <ModalContent {...props} {...bookingLoaderProps} onRequestClose={pop} />
    </BaseModal>
  );
}

interface ModalContentProps extends UseBookingLoaderResult {
  onRequestClose: () => void;
  render: (props: BookingDetailsResultProps) => ReactNode;
}

function ModalContent({
  booking,
  loading,
  error,
  onRequestClose,
  render,
}: ModalContentProps) {
  if (loading) {
    return (
      <ModalBody>
        <ScreenSpinner />
      </ModalBody>
    );
  }

  if (error || !booking) {
    const { message, icon } = getErrorModalContentFromGQLError(error);
    return (
      <ModalBody>
        <ModalHeader
          heading={message}
          onClickClose={onRequestClose}
          closeButtonAriaLabel={"Close"}
        />
        <BookingErrorModalContents icon={icon} />
      </ModalBody>
    );
  }

  return render({ booking, onRequestClose });
}
